'use client'
import { toast } from '@/components/common/toast'
import {
  useRemoveCartMiniItem,
  useRemoveGiftItem,
  useUpdateCartItemQuantity,
} from '@/hooks/react-query/cart/use-cart'
import { QUERY_KEYS } from '@/hooks/react-query/constants'
import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Minus, Plus, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

// Gom các lần bấm +/- liên tiếp thành 1 request cập nhật số lượng
const UPDATE_QUANTITY_DEBOUNCE_MS = 500

interface Props {
  id: string
  name: string
  image: string
  regularPrice: number
  variantTitle: string
  quantity: number
  link: string
  variantId: string
  indexId: string | null
}

export const CartMiniItem = ({
  id,
  name,
  image,
  regularPrice,
  variantTitle,
  quantity,
  link,
  variantId,
  indexId,
}: Props) => {
  const { mutateAsync: removeCartMiniItem, isPending: isRemovingCartMiniItem } =
    useRemoveCartMiniItem()
  const { mutateAsync: removeGiftItem, isPending: isRemovingGiftItem } =
    useRemoveGiftItem()
  const { mutate: updateCartItemQuantity } = useUpdateCartItemQuantity()
  const queryClient = useQueryClient()

  const t = useTranslations()

  // Detect if item is a gift (price 0 and has index_id)
  const isGiftItem = regularPrice === 0 && indexId !== null

  // Số lượng hiển thị tạm thời trong lúc chờ debounce/API, đồng bộ lại khi server trả về số mới
  const [localQuantity, setLocalQuantity] = useState(quantity)
  const [prevQuantity, setPrevQuantity] = useState(quantity)
  if (quantity !== prevQuantity) {
    setPrevQuantity(quantity)
    setLocalQuantity(quantity)
  }

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const handleRemoveItem = async () => {
    if (isGiftItem) {
      // Use removeGiftItem for gift items (same logic as cart page)
      await removeGiftItem({
        index_id: indexId!,
      })
    } else {
      // Use removeCartMiniItem for regular items
      await removeCartMiniItem({
        product_id: id,
        variants: variantId,
        box: '0',
        index_id: indexId,
        device_token: DeviceTokenHelper.get(),
      })
    }
  }

  const handleChangeQuantity = (delta: number) => {
    const nextQuantity = localQuantity + delta
    clearTimeout(debounceRef.current)

    if (nextQuantity < 1) {
      handleRemoveItem()
      return
    }

    setLocalQuantity(nextQuantity)
    debounceRef.current = setTimeout(() => {
      updateCartItemQuantity(
        {
          index_id: indexId,
          quantity: nextQuantity,
          product_id: id,
          variant_id: variantId,
          box: false,
          device_token: DeviceTokenHelper.get(),
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.CART_MINI],
            })
          },
          onError: (error) => {
            setLocalQuantity(quantity)
            toast.message({
              title: t('title.sorry'),
              message: error.message || t('cart_page.message.update_cart_fail'),
            })
          },
        }
      )
    }, UPDATE_QUANTITY_DEBOUNCE_MS)
  }

  // Show skeleton when removing (either type)
  if (isRemovingCartMiniItem || isRemovingGiftItem) {
    return <CartMiniItemSkeleton />
  }

  return (
    <div className='flex items-start gap-1 rounded-lg p-2'>
      <Link href={link} className='shrink-0'>
        <Image
          src={ImageHelper.getImageUrl(image, '', 200)}
          alt='cart-item'
          width={89}
          height={120}
          className='h-[120px] w-[89px] rounded-lg object-cover'
        />
      </Link>
      <div className='flex min-w-0 flex-1 flex-col justify-between gap-2 self-stretch pl-1'>
        <div className='flex flex-col gap-0.5'>
          <Link
            href={link}
            className='line-clamp-2 text-sm font-semibold leading-5 text-neutral-900 hover:text-primary'
          >
            {name}
          </Link>
          <p className='text-sm text-neutral-900/60'>{variantTitle}</p>
        </div>
        <p className='text-base font-semibold text-neutral-900'>
          {formatPrice(regularPrice)}
        </p>
        {!isGiftItem ? (
          <div className='flex h-7 w-fit items-center rounded-[29px] border-[0.5px] border-neutral-300 bg-light'>
            <button
              type='button'
              onClick={() => handleChangeQuantity(-1)}
              className='flex h-full w-7 items-center justify-center'
              aria-label={t('product_detail.title.decrease_quantity')}
              ga-tracking-value='navbar__cart_mini__quantity__decrement'
              ga-tracking-label='navbar__cart_mini__quantity__decrement'
            >
              <Minus size={12} className='text-neutral-900' />
            </button>
            <span className='w-6 text-center text-sm leading-5 text-neutral-900'>
              {String(localQuantity).padStart(2, '0')}
            </span>
            <button
              type='button'
              onClick={() => handleChangeQuantity(1)}
              className='flex h-full w-7 items-center justify-center'
              aria-label={t('product_detail.title.increase_quantity')}
              ga-tracking-value='navbar__cart_mini__quantity__increment'
              ga-tracking-label='navbar__cart_mini__quantity__increment'
            >
              <Plus size={12} className='text-neutral-900' />
            </button>
          </div>
        ) : (
          <p className='text-sm leading-4.5 text-neutral-800'>x{quantity}</p>
        )}
      </div>
      <button
        type='button'
        onClick={handleRemoveItem}
        className='flex h-5 w-5 shrink-0 items-center justify-center'
        aria-label={t('button.delete')}
      >
        <X size={16} className='text-neutral-400 hover:text-neutral-700' />
      </button>
    </div>
  )
}

// Skeleton component for cart mini item
export const CartMiniItemSkeleton = () => {
  return (
    <div className='flex items-start gap-2 p-2'>
      <div className='h-[120px] w-[89px] animate-pulse rounded-lg bg-neutral-200' />
      <div className='flex h-[120px] flex-1 flex-col justify-between'>
        <div>
          <div className='mb-2 h-4 animate-pulse rounded bg-neutral-200' />
          <div className='h-3 w-1/3 animate-pulse rounded bg-neutral-200' />
        </div>
        <div className='h-4 w-20 animate-pulse rounded bg-neutral-200' />
        <div className='h-7 w-24 animate-pulse rounded-full bg-neutral-200' />
      </div>
    </div>
  )
}
